import { FC } from 'react';
import {
  Center,
  Container,
  Flex,
  HStack,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';

// OCT-7340: TreatyApp: User Management — add an Action History view of admin activity - Frontend : ashish.atwal@resolutionlife.com
import { ActionHistoryButton } from '../../components/action-history/action-history-button';
import { AddUserButton } from '../../components/add-user/add-user-button';
import { RoleFilter } from '../../components/role-filter/role-filter';
import { ShowBlockedUserToggle } from '../../components/show-blocked-user-toggle/show-blocked-user-toggle';
import { UserSearchInput } from '../../components/user-search/user-search-input';
import { UserTypeButtonsToggle } from '../../components/user-type-toggle/user-type-toggle';
import * as Routes from '../../constants/routes';
import {
  useGetUserManagementDataQuery,
  UserType,
} from '../../graphql/generated';
import { ErrorState } from '../../ui/errors/error-state';
import { getAvailableDataRooms } from '../../utils/treaty-utils/get-available-data-rooms';
import { useTimedPolling } from './use-timed-polling';
import { useUserManagement } from './use-user-management';
import { UserResults } from './user-results';

type UserManagementPageParams = {
  name?: string;
  userType?: UserType;
  page: string;
};

export const navigateToUserManagementPage = (
  searchParams?: UserManagementPageParams
): string => {
  const path = Routes.USER_MANAGEMENT;

  if (searchParams) {
    const searchParamsString = searchParams.toString();
    return `${path}?${searchParamsString}`;
  }

  return path;
};

export const UserManagementPage: FC = () => {
  const {
    searchParams,
    currentPage,
    pageSize,
    setPage,
    setUserType,
    setName,
    setIncludeBlocked,
    setRoles,
  } = useUserManagement();

  const { data, error, loading, refetch } = useGetUserManagementDataQuery({
    variables: { searchParams },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  // Auth0 takes a few seconds to update,
  // so we need to poll after blocking, unblocking, or adding a user
  const { startTimedPolling } = useTimedPolling({
    fetchFn: () => { refetch(); },
    timeLimit: 3000,
    pollInterval: 500,
  });

  const handleUserTypeClick = (userType: UserType) => {
    setUserType(userType);
  };

  const handleShowBlockedUserClick = () => {
    setIncludeBlocked(!searchParams.includeBlocked);
  };

  const treaties = getAvailableDataRooms(data);

  const handleRoleFilterChange = (roleId: string) => {
    const currentRoles = searchParams.roles || [];
    const updatedRoles = currentRoles.includes(roleId)
      ? [...currentRoles].filter((x) => x !== roleId)
      : [...currentRoles, roleId];

    setRoles(updatedRoles);
  };

  const handleSelectAllRoles = () => {
    if (data?.roles) {
      setRoles(data.roles.map((role) => role.id));
    }
  };

  const handleClearRoleFilter = () => {
    setRoles([]);
  };

  return (
    <>
      <Helmet>
        <title>User Management</title>
      </Helmet>

      <Container p={8} maxW="100%" data-testid="user-management-page">
        <Flex justify="space-between" align="baseline">
          <Text textStyle="title" as="h1">
            User Management
          </Text>
          {/* OCT-7340: Action History button sits alongside Add User in the header */}
          <HStack spacing={4}>
            <ActionHistoryButton />
            <AddUserButton
              roles={data?.roles}
              treaties={treaties}
              startTimedPolling={startTimedPolling}
            />
          </HStack>
        </Flex>

        <Flex
          mt={12}
          direction={{ sm: 'column', md: 'row' }}
          gridGap={4}
          align="baseline"
          justify="space-between"
        >
          <HStack spacing={8}>
            <UserTypeButtonsToggle
              active={searchParams.userType}
              onClick={handleUserTypeClick}
            />
            <RoleFilter
              roles={data?.roles || []}
              selectedRoles={searchParams.roles || []}
              onChange={handleRoleFilterChange}
              onSelectAll={handleSelectAllRoles}
              onDeselectAll={handleClearRoleFilter}
            />
            <div>
              <ShowBlockedUserToggle
                onClick={handleShowBlockedUserClick}
                includeBlocked={searchParams.includeBlocked}
              />
            </div>
          </HStack>
          <UserSearchInput name={searchParams.name} setName={setName} />
        </Flex>

        {loading && (
          <Center pt="200px">
            <Spinner size="xl" />
          </Center>
        )}

        {!loading && error && (
          <ErrorState
            title="An error occurred while loading users"
            subtitle="Please reach out to the technical support team for further assistance."
            h="50vh"
          />
        )}

        {!loading && data && (
          <UserResults
            treatyUsers={data.treatyUsers}
            roles={data.roles}
            treaties={treaties}
            searchParams={searchParams}
            currentPage={currentPage}
            pageSize={pageSize}
            setPage={setPage}
            startTimedPolling={startTimedPolling}
          />
        )}
      </Container>
    </>
  );
};
